import type {
  LabelGroupSelection,
  VolumeLabelTextSelection,
} from '../../../lib/d3/d3SelectionTypes';
import {
  volumeBubbleTextLines,
  resolveVolumeLabelDisplayText,
  selectVisibleVolumeBubbleRows,
  sizeArrowRowFits,
  volumeLabelChordWidthPx,
  volumeBubbleLabelLayout,
  type VolumeBubbleTextLine,
} from '../../../lib/bubble/volumeBubbleLabels';
import {
  appendSizeArrowSvg,
  BUBBLE_SIZE_ARROW_HEIGHT,
  BUBBLE_SIZE_ARROW_WIDTH,
  SIZE_ARROW_GAP,
} from '../../../lib/bubble/sizeArrowIcon';
import { isVolumeManaged } from '../../../lib/classification';
import type { PackLayoutNode, PackNode } from '../../../lib/bubble/packNodeTypes';
import { setSvgTextFill } from '../../../lib/bubble/svgTextFill';
import type { ExplorationVolume } from '../../../types/exploration';
import { type VolumeLabelElement } from '../bubbleDomTypes';
import { reapplyVolumeLabelDimIfNeeded } from '../bubbleEmphasis';

export interface BubbleVolumeLabelLayer {
  applyVolumeLabelsForK: (k: number) => void;
}

const svgNs = 'http://www.w3.org/2000/svg';

interface VolumeLabelEntry {
  d: PackLayoutNode;
  g: LabelGroupSelection;
  iconG: LabelGroupSelection;
  lineEls: Partial<Record<'name' | 'id', VolumeLabelTextSelection>>;
  /** Single-size `<text>` or composite size `<g>` (original + arrow + current). */
  sizeHost: SVGGElement | SVGTextElement;
  /** Measured width of the managed size-arrow row (screen px at label scale). */
  sizeArrowWidth?: number;
  lines: VolumeBubbleTextLine[];
}

function markSkipCounterScale(el: Element | null) {
  if (el) (el as VolumeLabelElement).__skipCounterScale = true;
}

function appendPlainSizeText(parent: LabelGroupSelection): SVGTextElement {
  const el = parent
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('font-family', 'var(--font-sans)')
    .attr('pointer-events', 'none')
    .attr('data-line-key', 'size');
  markSkipCounterScale(el.node());
  return el.node() as SVGTextElement;
}

function appendSizeArrowHost(parent: LabelGroupSelection): SVGGElement {
  const host = document.createElementNS(svgNs, 'g') as SVGGElement;
  host.setAttribute('class', 'b-vol-size-arrow');
  host.setAttribute('pointer-events', 'none');
  host.setAttribute('data-line-key', 'size');
  markSkipCounterScale(host);
  parent.node()?.appendChild(host);
  return host;
}

function setSizeHostHidden(host: SVGGElement | SVGTextElement, hidden: boolean) {
  host.style.display = hidden ? 'none' : '';
}

function mkSizeText(
  host: SVGGElement,
  content: string,
  line: VolumeBubbleTextLine
): SVGTextElement {
  const t = document.createElementNS(svgNs, 'text');
  t.setAttribute('x', '0');
  t.setAttribute('y', '0');
  t.setAttribute('text-anchor', 'start');
  t.setAttribute('dominant-baseline', 'alphabetic');
  t.setAttribute('font-family', 'var(--font-sans)');
  t.setAttribute('font-size', String(line.fontSize));
  t.setAttribute('font-weight', String(line.fontWeight));
  t.setAttribute('font-variant-numeric', 'tabular-nums');
  t.setAttribute('fill', line.fill);
  t.setAttribute('pointer-events', 'none');
  t.textContent = content;
  setSvgTextFill(t, line.fill);
  markSkipCounterScale(t);
  host.appendChild(t);
  return t;
}

/**
 * Build the managed size row once (measure real glyph widths, then position).
 * Returns the measured total width for chord fit checks.
 */
function buildManagedSizeArrowRow(
  host: SVGGElement,
  line: VolumeBubbleTextLine,
  sizeArrow: { original: string; current: string }
): number {
  while (host.firstChild) host.removeChild(host.firstChild);

  const orig = mkSizeText(host, sizeArrow.original, line);
  const curr = mkSizeText(host, sizeArrow.current, line);

  const charFallback = (s: string) => s.length * line.fontSize * 0.55;
  const measure = (el: SVGTextElement, s: string) => {
    const w = el.getComputedTextLength();
    return w > 0 ? w : charFallback(s);
  };

  const origW = measure(orig, sizeArrow.original);
  const currW = measure(curr, sizeArrow.current);
  const totalW = origW + SIZE_ARROW_GAP + BUBBLE_SIZE_ARROW_WIDTH + SIZE_ARROW_GAP + currW;

  let cursor = -totalW / 2;
  orig.setAttribute('x', String(cursor));
  cursor += origW + SIZE_ARROW_GAP;

  // Alphabetic baseline is at y=0; center the glyph on the text’s optical mid (~0.35em).
  const arrowY = -(line.fontSize * 0.35 + BUBBLE_SIZE_ARROW_HEIGHT / 2);
  const arrow = appendSizeArrowSvg(host, {
    x: cursor,
    y: arrowY,
    width: BUBBLE_SIZE_ARROW_WIDTH,
    height: BUBBLE_SIZE_ARROW_HEIGHT,
    fill: line.fill,
  });
  host.insertBefore(arrow, curr);
  cursor += BUBBLE_SIZE_ARROW_WIDTH + SIZE_ARROW_GAP;

  curr.setAttribute('x', String(cursor));

  return totalW;
}

function positionSizeArrowRow(
  host: SVGGElement,
  layout: { x: number; y: number; invFit: number; k: number }
) {
  const scale = layout.invFit / layout.k;
  host.setAttribute('transform', `translate(${layout.x},${layout.y}) scale(${scale})`);
}

export function createBubbleVolumeLabelLayer(deps: {
  invFit: number;
  labelG: LabelGroupSelection;
  d3root: { descendants: () => PackNode[] };
  getHierChipIconNode: (key: string) => Element | null;
}): BubbleVolumeLabelLayer {
  const { invFit, labelG, d3root, getHierChipIconNode } = deps;

  const VOL_ICON_SIZE = 20;
  const VOL_ICON_PADDING = 10;
  const VOL_LINE_GAP = volumeBubbleLabelLayout.labelGap;
  const VOL_ICON_TEXT_GAP = volumeBubbleLabelLayout.iconTextGap;
  const _volumeLabels: VolumeLabelEntry[] = [];

  d3root.descendants().forEach((d) => {
    if (d.data.type !== 'volume') return;
    const v = d.data.volume as ExplorationVolume | undefined;
    if (!v) return;
    const lines = volumeBubbleTextLines(v);
    const g = labelG
      .append('g')
      .datum(d as PackLayoutNode)
      .attr('class', 'b-vol-label')
      .attr('data-managed', String(isVolumeManaged(v)))
      .attr('pointer-events', 'none');
    const iconG = g.append('g').attr('class', 'b-vol-icon').attr('pointer-events', 'none');
    markSkipCounterScale(iconG.node());
    const iconNode = getHierChipIconNode('volume');
    if (iconNode) {
      iconNode.setAttribute('x', String(-VOL_ICON_SIZE / 2));
      iconNode.setAttribute('y', String(-VOL_ICON_SIZE / 2));
      iconNode.setAttribute('width', String(VOL_ICON_SIZE));
      iconNode.setAttribute('height', String(VOL_ICON_SIZE));
      iconG.node()?.appendChild(iconNode);
    }
    const lineEls: VolumeLabelEntry['lineEls'] = {};
    let sizeHost: SVGGElement | SVGTextElement | null = null;
    let sizeArrowWidth: number | undefined;
    for (const line of lines) {
      if (line.key === 'size') {
        if (line.sizeArrow) {
          const host = appendSizeArrowHost(g);
          sizeArrowWidth = buildManagedSizeArrowRow(host, line, line.sizeArrow);
          sizeHost = host;
        } else {
          sizeHost = appendPlainSizeText(g);
        }
        continue;
      }
      const el = g
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('font-family', 'var(--font-sans)')
        .attr('pointer-events', 'none');
      markSkipCounterScale(el.node());
      lineEls[line.key] = el;
    }
    if (!sizeHost) return;
    _volumeLabels.push({
      d: d as PackLayoutNode,
      g,
      iconG,
      lineEls,
      sizeHost,
      sizeArrowWidth,
      lines,
    });
  });

  const lineHeightPx = (line: VolumeBubbleTextLine) => line.lineHeight;
  const iconHasMinPadding = (radius: number, totalH: number) => {
    if (totalH <= 0) return false;
    const half = VOL_ICON_SIZE / 2;
    const iconCenterY = -totalH / 2 + half;
    const iconTop = iconCenterY - half;
    if (radius + iconTop < VOL_ICON_PADDING) return false;
    const availHalfWidth = Math.sqrt(Math.max(0, radius * radius - iconCenterY * iconCenterY));
    return availHalfWidth >= half + VOL_ICON_PADDING;
  };

  function applyVolumeLabelsForK(k: number) {
    for (const l of _volumeLabels) {
      const { d, g, iconG, lineEls, sizeHost, sizeArrowWidth, lines } = l;
      const screenR = (d.r * k) / invFit;
      if (screenR < 9) {
        g.style('display', 'none');
        continue;
      }

      const cyVb = d.y;
      const maxStackH = screenR * 1.75;
      const hasIcon = !!iconG.node()?.firstChild;

      const stackHeightPx = (icon: boolean, vis: VolumeBubbleTextLine[]) => {
        let h = 0;
        if (icon) {
          h += VOL_ICON_SIZE;
          if (vis.length > 0) h += VOL_ICON_TEXT_GAP;
        }
        vis.forEach((line, i) => {
          h += lineHeightPx(line);
          if (i < vis.length - 1) h += VOL_LINE_GAP;
        });
        return h;
      };

      const iconOnlyHeight = () => stackHeightPx(true, []);
      const canShowIcon = () =>
        hasIcon && iconOnlyHeight() <= maxStackH && iconHasMinPadding(screenR, iconOnlyHeight());

      // Icon must be able to fit at all before any content renders; once past
      // that floor, rows drop cumulatively: Volume ID, then Name, then the
      // Icon itself, with Size the last thing ever hidden.
      let showIcon = false;
      let visible: VolumeBubbleTextLine[] = [];
      if (canShowIcon()) {
        const fits = (candidate: { showIcon: boolean; visible: VolumeBubbleTextLine[] }) => {
          const h = stackHeightPx(candidate.showIcon, candidate.visible);
          return h <= maxStackH && (!candidate.showIcon || iconHasMinPadding(screenR, h));
        };
        ({ showIcon, visible } = selectVisibleVolumeBubbleRows(lines, true, fits));
      }

      if (!showIcon && visible.length === 0) {
        g.style('display', 'none');
        iconG.style('display', 'none');
        for (const key of ['name', 'id'] as const) {
          lineEls[key]?.style('display', 'none');
        }
        setSizeHostHidden(sizeHost, true);
        continue;
      }

      const totalH = stackHeightPx(showIcon, visible);
      let yPx = -totalH / 2;

      if (showIcon && iconG.node()?.firstChild) {
        const iconCenterY = yPx + VOL_ICON_SIZE / 2;
        iconG
          .style('display', '')
          .attr(
            'transform',
            `translate(${d.x},${cyVb + (iconCenterY * invFit) / k}) scale(${invFit / k})`
          );
        yPx += VOL_ICON_SIZE + VOL_ICON_TEXT_GAP;
      } else {
        iconG.style('display', 'none');
      }

      let anyVisible = false;
      for (const line of lines) {
        const idx = visible.indexOf(line);
        if (line.key === 'size') {
          if (idx === -1) {
            setSizeHostHidden(sizeHost, true);
            continue;
          }
          const charW = line.fontSize * 0.55;
          const baselinePx = yPx + line.fontSize * 0.85;
          const dyFromCenter = baselinePx - totalH / 2;
          const chord = volumeLabelChordWidthPx(screenR, dyFromCenter);

          if (line.sizeArrow && sizeHost instanceof SVGGElement) {
            if (!sizeArrowRowFits(line.sizeArrow, charW, chord, sizeArrowWidth)) {
              setSizeHostHidden(sizeHost, true);
              continue;
            }
            anyVisible = true;
            setSizeHostHidden(sizeHost, false);
            positionSizeArrowRow(sizeHost, {
              x: d.x,
              y: cyVb + (baselinePx * invFit) / k,
              invFit,
              k,
            });
            yPx += lineHeightPx(line) + VOL_LINE_GAP;
            continue;
          }

          if (!(sizeHost instanceof SVGTextElement)) {
            setSizeHostHidden(sizeHost, true);
            continue;
          }
          const text = resolveVolumeLabelDisplayText(line, charW, chord);
          if (!text) {
            setSizeHostHidden(sizeHost, true);
            continue;
          }
          anyVisible = true;
          setSizeHostHidden(sizeHost, false);
          sizeHost.setAttribute('x', String(d.x));
          sizeHost.setAttribute('y', String(cyVb + (baselinePx * invFit) / k));
          sizeHost.setAttribute('font-size', String((line.fontSize * invFit) / k));
          sizeHost.setAttribute('font-weight', String(line.fontWeight));
          sizeHost.setAttribute('fill', line.fill);
          sizeHost.setAttribute('font-variant-numeric', line.tabular ? 'tabular-nums' : '');
          sizeHost.setAttribute('text-anchor', 'middle');
          sizeHost.setAttribute('font-family', 'var(--font-sans)');
          sizeHost.textContent = text;
          setSvgTextFill(sizeHost, line.fill);
          yPx += lineHeightPx(line) + VOL_LINE_GAP;
          continue;
        }

        const el = lineEls[line.key];
        if (!el) continue;
        if (idx === -1) {
          el.style('display', 'none');
          continue;
        }
        const charW = line.fontSize * 0.55;
        const baselinePx = yPx + line.fontSize * 0.85;
        const dyFromCenter = baselinePx - totalH / 2;
        const chord = volumeLabelChordWidthPx(screenR, dyFromCenter);
        const text = resolveVolumeLabelDisplayText(line, charW, chord);
        if (!text) {
          el.style('display', 'none');
          continue;
        }
        anyVisible = true;
        const textNode = el.node() as SVGTextElement | null;
        el.style('display', '')
          .attr('x', d.x)
          .attr('y', cyVb + (baselinePx * invFit) / k)
          .attr('font-size', (line.fontSize * invFit) / k)
          .attr('font-weight', line.fontWeight)
          .attr('fill', line.fill)
          .attr('font-variant-numeric', line.tabular ? 'tabular-nums' : null)
          .attr('data-line-key', line.key)
          .text(text);
        if (textNode) setSvgTextFill(textNode, line.fill);
        yPx += lineHeightPx(line) + VOL_LINE_GAP;
      }

      g.style('display', anyVisible || showIcon ? '' : 'none');
      reapplyVolumeLabelDimIfNeeded(g.node() as SVGElement);
    }
  }

  return { applyVolumeLabelsForK };
}
